import React, { useCallback } from 'react';
import { Space, Card, Flex } from 'antd';
import { ExpenseDataProvider } from './components/ExpenseDataProvider';
import { ExpenseActions } from './components/ExpenseActions';
import { ExpenseFormFields } from './components/ExpenseFormFields';
import { useExpenseFormState } from './hooks/useExpenseFormState';
import { ExpenseStatus } from './components/ExpenseStatus';
import { useTranslation } from 'react-i18next';
import PageTitle from 'src/components/PageTitle';

/**
 * ExpenseForm Component
 *
 * Expense form component with optimized architecture.
 * @returns Optimized React functional component for expense form management
 */
const ExpenseForm: React.FC = () => {
  const {
    permissions,
    profile,
    expenseSettings,
    accessToken,
    clientId,
    recordToEdit,
    submitterInfo,
    isPrivateCard,
    isMyExpense,
    isExpenseOwnerChangeAvailable,
    onPrivateCardChange,
    onOwnerSwitchChange,
    updateRecordToEdit,
    setSubmitterInfo,
  } = useExpenseFormState();

  const { t } = useTranslation();

  const handleSave = useCallback(
    (dataProps: {
      setApproving: (value: boolean) => void;
      status: string;
      handleCreateExpense: () => Promise<void>;
      handleUpdateExpense: () => Promise<void>;
    }) => {
      dataProps.setApproving(false);
      dataProps.status === ''
        ? dataProps.handleCreateExpense()
        : dataProps.handleUpdateExpense();
    },
    []
  );

  const handleApprove = useCallback(
    (dataProps: { handleApproveExpense: () => Promise<void> }) => {
      dataProps.handleApproveExpense();
    },
    []
  );

  /**
   * Generate dynamic title based on expense status and ownership
   */
  const getTitle = (status: string): string => {
    const expenseText = isMyExpense
      ? t('My expense').toLocaleLowerCase()
      : t('Expense').toLocaleLowerCase();

    switch (status) {
      case 'sent':
        return t('View/See') + ' ' + expenseText;
      case 'draft':
        return t('Edit') + ' ' + expenseText;
      case 'failure':
        return t('Edit') + ' ' + expenseText;
      default:
        return t('Create') + ' ' + expenseText;
    }
  };

  return (
    <ExpenseDataProvider
      accessToken={accessToken}
      clientId={clientId}
      profile={profile}
      expenseSettings={expenseSettings}
      recordToEdit={recordToEdit}
      isMyExpense={isMyExpense}
      isPrivateCard={isPrivateCard}
      updateRecordToEdit={updateRecordToEdit}
    >
      {(expenseState) => (
        <Card
          title={
            <Flex justify="space-between">
              <PageTitle text={getTitle(expenseState.status)} />
            </Flex>
          }
          type="inner"
        >
          <Space direction="vertical" size="small" style={{ display: 'flex' }}>
            <ExpenseStatus
              status={expenseState.status}
              submitterInfo={submitterInfo}
            />
            <ExpenseFormFields
              permissions={permissions}
              profile={profile}
              expenseSettings={expenseSettings}
              recordToEdit={recordToEdit}
              isMyExpense={isMyExpense}
              isExpenseOwnerChangeAvailable={isExpenseOwnerChangeAvailable}
              isPrivateCard={isPrivateCard}
              onOwnerSwitchChange={onOwnerSwitchChange}
              onPrivateCardChange={onPrivateCardChange}
              setsubmitterInfo={setSubmitterInfo}
              {...expenseState}
            />

            <ExpenseActions
              status={expenseState.status}
              formChanged={expenseState.formChanged}
              submitting={expenseState.submitting}
              onSave={() => handleSave(expenseState)}
              onApprove={() => handleApprove(expenseState)}
            />
          </Space>
        </Card>
      )}
    </ExpenseDataProvider>
  );
};

export default ExpenseForm;
